import { isObject, valueEquals } from './util';
import { deepmergeCustom } from 'deepmerge-ts';

const customMerge = deepmergeCustom({
  mergeArrays: false, // arrays from the "later" object simply replace earlier ones
});

export function applyAppend(currentValue: unknown, outputValue: unknown, pathCreated: boolean, jsonPath: string) {
  if (outputValue === null || outputValue === undefined) {
    // If the new value is null, no need to do anything
    return currentValue;
  }
  // Check if we actually have an array
  if (Array.isArray(currentValue)) {
    // If so, add the output value(s) to the current array
    if (Array.isArray(outputValue)) {
      currentValue.push(...(outputValue as unknown[]));
    } else {
      currentValue.push(outputValue);
    }
    return currentValue;
  } else if (currentValue === null || currentValue === undefined || pathCreated) {
    // No current value.
    // Or, the path was created and is possibly an empty object.
    // Create a new array with the output.
    if (Array.isArray(outputValue)) return outputValue;
    return [outputValue];
  } else {
    // Not an array at all!
    console.warn(`Path [${jsonPath}] is not an array! Append operation only works with arrays.`);
    return currentValue;
  }
}

export function applyMerge(currentValue: unknown, outputvalue: unknown) {
  // Merging two objects
  if (!isObject(outputvalue)) {
    // If the new value is not an object or is null, no need to do anything
    return currentValue;
  }
  if (typeof currentValue !== 'object' && currentValue !== undefined) {
    // if the old value is not an object, can't merge
    return currentValue;
  }
  return customMerge(currentValue, outputvalue);
}

export function applyRemoveKey(currentValue: unknown, outputValue: unknown, jsonPath: string) {
  if (Array.isArray(currentValue)) {
    if (typeof outputValue === 'number' && !Number.isNaN(outputValue)) {
      currentValue.splice(outputValue, 1);
    } else if (outputValue !== null && outputValue !== undefined) {
      console.warn(`Path [${jsonPath}]: Output value needs to be a number to remove an item from an array.`);
    }
  } else if (isObject(currentValue)) {
    if (typeof outputValue === 'string') {
      // @ts-expect-error unknown keys
      delete currentValue[outputValue];
    } else if (outputValue !== null && outputValue !== undefined) {
      // allow null outputvalue when nothing needs to be deleted, but output is required
      console.warn(`Path [${jsonPath}]: Output value needs to be a string to remove a property from an object`);
    }
  } else {
    console.warn(`Path [${jsonPath}] is not an array or object! RemoveKey can not be used with other types.`);
  }
  return currentValue;
}

export function applyRemoveKeys(currentValue: unknown, outputValue: unknown, jsonPath: string) {
  if (!Array.isArray(outputValue)) {
    console.warn(`Path [${jsonPath}]: Output value needs to be an array for RemoveKeys `);
    return currentValue;
  }

  if (Array.isArray(currentValue)) {
    const isNumberArray = outputValue.every(
      (value): value is number => typeof value === 'number' && !Number.isNaN(value),
    );
    if (isNumberArray) {
      for (const value of outputValue) {
        currentValue.splice(value, 1);
      }
    } else {
      console.warn(
        `Path [${jsonPath}]: Output value array contains non number values. Numbers are needed to remove by indices from an array`,
      );
    }
  } else if (isObject(currentValue)) {
    const isStringArray = outputValue.every((value) => typeof value === 'string');
    if (isStringArray) {
      for (const value of outputValue) {
        // @ts-expect-error unknown keys
        delete currentValue[value];
      }
    } else {
      console.warn(
        `Path [${jsonPath}]: Output value array contains non string values. Strings are needed to remove by keys from an object`,
      );
    }
  }
  return currentValue;
}

export function applyRemoveValue(currentValue: unknown, outputValue: unknown) {
  if (Array.isArray(currentValue)) {
    return currentValue.filter((item) => !valueEquals(item, outputValue));
  } else if (isObject(currentValue)) {
    const clone = { ...currentValue };
    for (const [key, item] of Object.entries(clone)) {
      if (valueEquals(item, outputValue)) {
        // @ts-expect-error unknown!
        delete clone[key];
      }
    }
    return clone;
  }
  return currentValue;
}

export function applyRemoveValues(currentValue: unknown, outputValue: unknown, jsonPath: string) {
  if (!Array.isArray(outputValue)) {
    console.warn(`Path [${jsonPath}]: Output value needs to be an array for RemoveValues `);
    return currentValue;
  }

  if (Array.isArray(currentValue)) {
    let outArray = currentValue;
    for (const value of outputValue) {
      outArray = outArray.filter((item) => !valueEquals(item, value));
    }
    return outArray;
  } else if (isObject(currentValue)) {
    // Shallow clone is enough
    const outObject = { ...currentValue };
    for (const value of outputValue) {
      for (const [key, item] of Object.entries(outObject)) {
        if (valueEquals(item, value)) {
          // @ts-expect-error unknown object shape
          delete outObject[key];
        }
      }
    }
    return outObject;
  }

  return currentValue;
}

export function applyInsert(currentValue: unknown, outputValue: unknown, pathCreated: boolean, jsonPath: string) {
  if (outputValue === null || outputValue === undefined) {
    // If the new value is null, no need to do anything
    return currentValue;
  }
  let index;
  let value;
  if (Array.isArray(outputValue)) {
    // Check that outputValue contains data in correct format
    [index, value] = outputValue;
    if (typeof index !== 'number' || Number.isNaN(index) || value === null || value === undefined) {
      console.log('outputValue should be an array of two items (<index>, <value-to-insert>)', outputValue);
      return currentValue;
    }
  } else {
    console.log('outputValue should be an array of two items (<index>, <value-to-insert>)', outputValue);
    return currentValue;
  }

  // Check if we actually have an array
  if (Array.isArray(currentValue)) {
    // If so, insert the output value(s) to the current array
    currentValue.splice(index, 0, value);
    return currentValue;
  } else if (currentValue === null || currentValue === undefined || pathCreated) {
    // No current value.
    // Or, the path was created and is possibly an empty object.
    // Create a new array with the output.
    // We disregard the `index` and just return the value in an array.
    return [value];
  } else {
    // Not an array at all!
    console.warn(`Path [${jsonPath}] is not an array! Insert operation only works with arrays.`);
    return currentValue;
  }
}
